'use client'

/**
 * Saved + Compare — real state for the header buttons, the panels, the card
 * hearts and the compare checkboxes (owner 09/02/2026: "the saved and compare
 * need to work better").
 *
 * Storage: localStorage, three keys.
 *   fridge_saved_products           — the saved list (any length)
 *   fridge_comparison_products      — the EXISTING key components/comparison-bar.tsx
 *                                     reads (max 4). Writing it here means the compare
 *                                     tray on product and collection pages shows the
 *                                     same list, and /compare?products=… opens it.
 *   fridge_comparison_products_meta — the brand/FIN of each compare row, which the
 *                                     legacy shape has no slot for.
 * Storage reads happen after hydration (useSyncExternalStore), never in an
 * effect that sets state, and every access is try/catch-guarded.
 *
 * SAME-TAB SYNC (FRIDGE-873). `subscribe()` used to listen ONLY to the cross-tab
 * `storage` event, which a page's own writes never fire — so a compare tick made
 * by the legacy bar on a product or collection page left the header badge stale
 * until a reload. Every write now goes through this module's writer functions,
 * which call `notifySavedCompare()`: that runs the in-process listeners AND
 * dispatches the `fridge:saved-compare` window event, which `subscribe()` also
 * listens for. Any other writer (a lazily-loaded chunk, a future surface) only
 * has to call `notifySavedCompare()` to update every badge in the tab.
 *
 * IDENTITY (FRIDGE-874). The homepage rails key a row by `products.id`; a
 * collection, brand, filter or deals card has only the canonical path. `matches()`
 * therefore treats a row as the same product when EITHER the id or the canonical
 * path agrees, so saving from a rail and ticking Compare on a collection card can
 * never produce two entries for one refrigerator.
 *
 * This file is the STORE only (React + localStorage, no images, no sprite, no
 * price formatting) so the site header and every product card can read and write
 * it on any route without pulling the homepage package into that page's
 * first-load JS (bundle-budget, 09/03/2026). The panels live in ./saved-compare.tsx.
 */
import * as React from 'react'

export interface SavedItem {
  id: string
  fin: string
  brand: string
  title: string
  image: string
  href: string
  priceCents: number
}

const SAVED_KEY = 'fridge_saved_products'
const COMPARE_KEY = 'fridge_comparison_products'
const COMPARE_META_KEY = 'fridge_comparison_products_meta'
export const COMPARE_MAX = 4

/**
 * The window event every writer fires and every reader listens for. The browser's
 * own `storage` event is cross-TAB only — it is never delivered to the document
 * that made the write — so without this a badge in the same tab stays stale.
 */
export const SAVED_COMPARE_EVENT = 'fridge:saved-compare'

/** The shape comparison-bar.tsx stores (id, title, image, canonical_url, price in dollars). */
interface LegacyCompareItem {
  id: string
  title: string
  image: string
  canonical_url: string
  price?: number
}

/** What identifies a product across surfaces: its row id, its canonical path, or both. */
export type ItemKey = string | { id?: string | null; href?: string | null }

function normHref(href: string | null | undefined): string {
  if (!href) return ''
  const path = href.split('?')[0].split('#')[0]
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}
function keyOf(k: ItemKey): { id: string; href: string } {
  if (typeof k === 'string') return { id: k, href: '' }
  return { id: (k.id ?? '').trim(), href: normHref(k.href) }
}
/** A stored row IS the product `k` names when either the id or the canonical path agrees. */
function matches(row: { id: string; href?: string; canonical_url?: string }, k: ItemKey): boolean {
  const q = keyOf(k)
  if (q.id && row.id === q.id) return true
  const rowHref = normHref(row.href ?? row.canonical_url)
  return !!q.href && !!rowHref && rowHref === q.href
}

function read<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    return []
  }
}
function write(key: string, value: unknown) {
  try {
    if (Array.isArray(value) && value.length === 0) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage blocked or full — the in-memory list still works for this page */
  }
}

const listeners = new Set<() => void>()

/**
 * Tell every badge, panel and card in THIS tab that the lists changed. Exported
 * because writers outside this module (components/comparison-bar.tsx) must be
 * able to announce their writes; `subscribe()` below also listens for the window
 * event, so a chunk holding its own copy of this module still hears it.
 */
export function notifySavedCompare() {
  listeners.forEach((l) => l())
  try {
    window.dispatchEvent(new CustomEvent(SAVED_COMPARE_EVENT))
  } catch {
    /* no window (server render) — the in-process listeners above already ran */
  }
}

/**
 * Subscribe to BOTH change channels: the browser's cross-tab `storage` event and
 * this module's own same-tab `fridge:saved-compare` event. Exported so the guard
 * test can prove a same-tab write really reaches a subscriber (FRIDGE-873) —
 * the bug was that only the cross-tab half existed.
 */
export function subscribeSavedCompare(l: () => void) {
  listeners.add(l)
  const onStorage = (e: StorageEvent) => {
    if (e.key === SAVED_KEY || e.key === COMPARE_KEY || e.key === COMPARE_META_KEY) l()
  }
  const onSameTab = () => l()
  window.addEventListener('storage', onStorage)
  window.addEventListener(SAVED_COMPARE_EVENT, onSameTab)
  return () => {
    listeners.delete(l)
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(SAVED_COMPARE_EVENT, onSameTab)
  }
}

let snapshotCache: { saved: SavedItem[]; compare: SavedItem[]; raw: string } | null = null

function lsRead(key: string): string {
  try {
    return localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function getSnapshot() {
  const raw = lsRead(SAVED_KEY) + ' ' + lsRead(COMPARE_KEY) + ' ' + lsRead(COMPARE_META_KEY)
  if (snapshotCache && snapshotCache.raw === raw) return snapshotCache
  const saved = read<SavedItem>(SAVED_KEY)
  const compare = readCompare()
  snapshotCache = { saved, compare, raw }
  return snapshotCache
}
const EMPTY = { saved: [] as SavedItem[], compare: [] as SavedItem[], raw: '' }
function getServerSnapshot() {
  return EMPTY
}

/* ------------------------------------------------------------------------- *
 * Plain readers and writers. ONE writer per key, so the legacy list and its
 * `_meta` sidecar can never drift apart and every write announces itself.
 * ------------------------------------------------------------------------- */

export function readSaved(): SavedItem[] {
  return read<SavedItem>(SAVED_KEY)
}

/** The compare list in SavedItem shape — the legacy rows, enriched from `_meta`. */
export function readCompare(): SavedItem[] {
  const rows = read<LegacyCompareItem>(COMPARE_KEY).map<SavedItem>((c) => ({
    id: c.id,
    fin: '',
    brand: '',
    title: c.title,
    image: c.image,
    href: c.canonical_url,
    priceCents: typeof c.price === 'number' ? Math.round(c.price * 100) : 0,
  }))
  // the FIN/brand of a compare row is kept beside it because the legacy shape
  // has no slot for either.
  const meta = read<SavedItem>(COMPARE_META_KEY)
  for (const c of rows) {
    const m = meta.find((x) => matches(x, { id: c.id, href: c.href }))
    if (m) Object.assign(c, { fin: m.fin, brand: m.brand })
  }
  return rows
}

export function isSaved(key: ItemKey): boolean {
  return readSaved().some((s) => matches(s, key))
}
export function isCompared(key: ItemKey): boolean {
  return read<LegacyCompareItem>(COMPARE_KEY).some((c) => matches(c, key))
}

export function toggleSavedItem(item: SavedItem) {
  const cur = readSaved()
  const next = cur.some((s) => matches(s, item)) ? cur.filter((s) => !matches(s, item)) : [...cur, item]
  write(SAVED_KEY, next)
  notifySavedCompare()
}
export function removeSavedItem(key: ItemKey) {
  write(SAVED_KEY, readSaved().filter((s) => !matches(s, key)))
  notifySavedCompare()
}
export function clearSavedItems() {
  write(SAVED_KEY, [])
  notifySavedCompare()
}

function toLegacy(item: SavedItem): LegacyCompareItem {
  return { id: item.id, title: item.title, image: item.image, canonical_url: item.href, price: item.priceCents / 100 }
}

/** Add when absent, remove when present. Never exceeds COMPARE_MAX (oldest drops). */
export function toggleCompareItem(item: SavedItem) {
  const cur = read<LegacyCompareItem>(COMPARE_KEY)
  const meta = read<SavedItem>(COMPARE_META_KEY)
  let next: LegacyCompareItem[]
  let nextMeta: SavedItem[]
  if (cur.some((c) => matches(c, item))) {
    next = cur.filter((c) => !matches(c, item))
    nextMeta = meta.filter((m) => !matches(m, item))
  } else {
    next = [...cur, toLegacy(item)].slice(-COMPARE_MAX)
    nextMeta = [...meta.filter((m) => !matches(m, item)), item].slice(-COMPARE_MAX)
  }
  write(COMPARE_KEY, next)
  write(COMPARE_META_KEY, nextMeta)
  notifySavedCompare()
}

/** Add without removing — the `comparison:add` event's semantics (a no-op at the cap). */
export function addCompareItem(item: SavedItem) {
  const cur = read<LegacyCompareItem>(COMPARE_KEY)
  if (cur.some((c) => matches(c, item)) || cur.length >= COMPARE_MAX) return
  write(COMPARE_KEY, [...cur, toLegacy(item)])
  write(COMPARE_META_KEY, [...read<SavedItem>(COMPARE_META_KEY).filter((m) => !matches(m, item)), item])
  notifySavedCompare()
}

export function removeCompareItem(key: ItemKey) {
  write(COMPARE_KEY, read<LegacyCompareItem>(COMPARE_KEY).filter((c) => !matches(c, key)))
  write(COMPARE_META_KEY, read<SavedItem>(COMPARE_META_KEY).filter((m) => !matches(m, key)))
  notifySavedCompare()
}
export function clearCompareItems() {
  write(COMPARE_KEY, [])
  write(COMPARE_META_KEY, [])
  notifySavedCompare()
}

export function useSavedCompare() {
  const snap = React.useSyncExternalStore(subscribeSavedCompare, getSnapshot, getServerSnapshot)
  const api = React.useMemo(
    () => ({
      isSaved: (key: ItemKey) => snap.saved.some((s) => matches(s, key)),
      isCompared: (key: ItemKey) => snap.compare.some((s) => matches(s, key)),
      toggleSaved: toggleSavedItem,
      removeSaved: removeSavedItem,
      clearSaved: clearSavedItems,
      toggleCompare: toggleCompareItem,
      removeCompare: removeCompareItem,
      clearCompare: clearCompareItems,
    }),
    [snap]
  )
  return { saved: snap.saved, compare: snap.compare, ...api }
}

/** `/compare?products=<slug,slug>` — the existing compare tool reads product slugs. */
export function compareHref(items: SavedItem[]): string {
  const slugs = items.map((i) => i.href.split('/').filter(Boolean).pop() ?? '').filter(Boolean)
  return slugs.length ? `/compare?products=${encodeURIComponent(slugs.join(','))}` : '/compare'
}
